import { GemBoxPdfViewer } from '@gembox/pdfviewer';
import '@gembox/pdfviewer/dist/es/pdfviewer.css';
import React, { useEffect, useRef, forwardRef } from 'react';

interface PdfViewerProps {
  data: string | Blob | null;
  style?: React.CSSProperties;
  /** Open the document at this 1-based page number when loaded */
  initialPage?: number;
}

interface PdfViewerRef {
  setPage: (pageNumber: number) => void;
}

export const PdfViewer = forwardRef<PdfViewerRef, PdfViewerProps>(
  ({ data, style, initialPage }, ref) => {
    const viewerElement = useRef<HTMLDivElement>(null);
    const urlRef = useRef<string | undefined>(undefined);
    const viewerRef = useRef<any>(null);

    useEffect(() => {
      const licenseKey = import.meta.env.VITE_GEMBOX_LICENSE_KEY;
      if (!licenseKey) {
        console.warn('GemBox license key not found in environment variables');
      }
      GemBoxPdfViewer.setLicense(licenseKey || 'FREE-LIMITED-KEY');
    }, []);

    useEffect(() => {
      if (!data || !viewerElement.current) {
        return;
      }

      const initializeViewer = async () => {
        try {
          let pdfUrl: string;

          // Handle different data types
          if (data instanceof Blob) {
            // If data is a Blob, create object URL directly
            pdfUrl = URL.createObjectURL(data);
            urlRef.current = pdfUrl;
          } else if (typeof data === 'string') {
            // Check if it's a URL (starts with http:// or https://)
            if (
              data.startsWith('http://') ||
              data.startsWith('https://') ||
              data.startsWith('blob:')
            ) {
              // It's a URL, use it directly
              pdfUrl = data;
            } else {
              // Assume it's base64 encoded data
              try {
                const binary = atob(data);
                const binaryData = new Uint8Array(binary.length);
                for (let i = 0; i < binary.length; i++) {
                  binaryData[i] = binary.charCodeAt(i);
                }
                const blob = new Blob([binaryData], {
                  type: 'application/pdf',
                });
                pdfUrl = URL.createObjectURL(blob);
                urlRef.current = pdfUrl;
              } catch (error) {
                console.error('Error decoding base64 data:', error);
                return;
              }
            }
          } else {
            console.error('Invalid PDF data type');
            return;
          }

          // Clean up existing viewer if any
          if (viewerRef.current) {
            try {
              viewerRef.current.destroy?.();
            } catch (error) {
              console.error('Error destroying PDF viewer:', error);
            }
            viewerRef.current = null;
          }

          // Clear the container
          if (viewerElement.current) {
            viewerElement.current.innerHTML = '';
          }

          // Create new viewer
          viewerRef.current = await GemBoxPdfViewer.create({
            container: viewerElement.current as HTMLElement,
            visibility: {
              sideMenu: true,
              open: false,
              print: true,
              pageNavigation: true,
              panMode: true,
              zoom: true,
              search: true,
            },
            initialDocument: pdfUrl,
          });

          // Navigate to initial page if specified (1-based)
          if (
            typeof initialPage === 'number' &&
            initialPage >= 1 &&
            viewerRef.current?.setCurrentPage
          ) {
            viewerRef.current.setCurrentPage(initialPage);
          }
        } catch (error) {
          console.error('Error creating PDF viewer:', error);
        }
      };

      initializeViewer();

      return () => {
        // Cleanup viewer
        if (viewerRef.current) {
          try {
            viewerRef.current.destroy?.();
          } catch (error) {
            console.error('Error destroying PDF viewer:', error);
          }
          viewerRef.current = null;
        }

        // Cleanup URL
        if (urlRef.current) {
          const urlToRevoke = urlRef.current;
          setTimeout(() => {
            URL.revokeObjectURL(urlToRevoke);
          }, 100);
          urlRef.current = undefined;
        }
      };
    }, [data, initialPage]);

    useEffect(() => {
      if (ref && typeof ref === 'object' && 'current' in ref) {
        ref.current = {
          setPage: (pageNumber: number) => {
            if (viewerRef.current) {
              viewerRef.current.setCurrentPage(pageNumber);
            }
          },
        };
      }
    }, [ref]);

    return (
      <div
        ref={viewerElement}
        style={{
          height: 'calc(90vh - 40px)',
          width: '100%',
          ...style,
        }}
      ></div>
    );
  },
);

PdfViewer.displayName = 'PdfViewer';
